import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Printer } from 'lucide-react';
import { ZONES } from '../types';
import { printWeek } from '../print/printWeek';
import { ZoneIcon } from './ZoneIcon';

/**
 * Print the Active Operating Week — the full sheet, or any single zone.
 * "Screen to plan, paper to execute": many people work best from a printed
 * page on the desk, so this is a first-class feature, not an afterthought.
 */
export function PrintButton() {
  const [open, setOpen] = useState(false);

  const run = (filter: Parameters<typeof printWeek>[0]) => {
    setOpen(false);
    printWeek(filter);
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
      >
        <Printer size={15} className="text-brand-600 dark:text-brand-400" />
        Print this week
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-2 sm:grid-cols-2">
              <button
                onClick={() => run('all')}
                className="rounded-lg bg-brand-600 px-3 py-2.5 text-left text-[11px] font-bold text-white transition-colors hover:bg-brand-500 sm:col-span-2"
              >
                🖨 Full week — quadrant overview + all four zones
              </button>
              {ZONES.map((z) => (
                <button
                  key={z.id}
                  onClick={() => run(z.id)}
                  className={`flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-left text-[11px] font-bold transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${z.color.text}`}
                >
                  <ZoneIcon zone={z.id} size={13} className={z.color.text} />
                  {z.title} only
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
